/**
 * Which span the page shows: Day / 7 days / 30 days / Custom, stepped back
 * and forward, with "Today". Writes the query; the page reads it from the URL.
 */
import React from "react";
import { styled } from "styled-components";
import { FleetQuery, Span, spanBounds, stepSpan } from "./query";
import { shiftDay, todayIso } from "./time";
import { spanTitle } from "./labels";
import { tokens } from "../TractorState/tokens";

interface SpanControlProps {
  query: FleetQuery;
  onChange: (next: FleetQuery) => void;
}

const SPANS: { value: Span; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "custom", label: "Custom" },
];

const SpanControl: React.FC<SpanControlProps> = ({ query, onChange }) => {
  const bounds = spanBounds(query);
  const atToday = query.to >= todayIso();
  const pick = (span: Span) => {
    const next: FleetQuery = { ...query, span };
    if (span === "custom") next.from = bounds.from;
    else delete next.from;
    onChange(next);
  };
  return (
    <Wrap>
      <Row>
        <Segmented role="group" aria-label="Span">
          {SPANS.map((s) => (
            <SegmentButton
              key={s.value}
              type="button"
              aria-pressed={query.span === s.value}
              onClick={() => pick(s.value)}
            >
              {s.label}
            </SegmentButton>
          ))}
        </Segmented>
        <Nav>
          <IconButton
            type="button"
            aria-label="Previous span"
            onClick={() => onChange(stepSpan(query, -1))}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M10 3 5 8l5 5" />
            </svg>
          </IconButton>
          <Title>{spanTitle(bounds.from, bounds.to)}</Title>
          <IconButton
            type="button"
            aria-label="Next span"
            disabled={atToday}
            onClick={() => onChange(stepSpan(query, 1))}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="m6 3 5 5-5 5" />
            </svg>
          </IconButton>
          {!atToday && (
            <TodayButton
              type="button"
              onClick={() => onChange({ ...query, to: todayIso() })}
            >
              Today
            </TodayButton>
          )}
        </Nav>
      </Row>
      {query.span === "custom" && (
        <Row>
          <DateLabel htmlFor="span-from">From</DateLabel>
          <DateInput
            id="span-from"
            type="date"
            value={bounds.from}
            max={bounds.to}
            onChange={(e) =>
              e.target.value && onChange({ ...query, from: e.target.value })
            }
          />
          <DateLabel htmlFor="span-to">To</DateLabel>
          <DateInput
            id="span-to"
            type="date"
            value={bounds.to}
            min={bounds.from}
            max={todayIso()}
            onChange={(e) =>
              e.target.value &&
              onChange({
                ...query,
                to: e.target.value,
                from:
                  bounds.from <= e.target.value
                    ? bounds.from
                    : shiftDay(e.target.value, -6),
              })
            }
          />
        </Row>
      )}
    </Wrap>
  );
};

export default SpanControl;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  @media (max-width: 760px) {
    align-items: stretch;
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

// Previous, the span's dates, next and Today stay on one line together: at a
// phone width the group wraps under the span chips as a whole instead of the
// dates breaking into a column and the arrows landing on their own lines.
const Nav = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 10px;
`;

const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid ${tokens.inputLine};
  border-radius: 8px;
  overflow: hidden;
  background: ${tokens.white};
`;

const SegmentButton = styled.button`
  border: 0;
  background: ${tokens.white};
  padding: 7px 12px;
  font: 600 12px ${tokens.fontSans};
  color: ${tokens.slate};
  cursor: pointer;
  min-height: 36px;
  &[aria-pressed="true"] {
    background: ${tokens.navy};
    color: ${tokens.white};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: -2px;
  }
`;

const IconButton = styled.button`
  border: 1px solid ${tokens.inputLine};
  background: ${tokens.white};
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${tokens.ink};
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const Title = styled.div`
  font: 600 14px ${tokens.fontSans};
  color: ${tokens.ink};
  min-width: 200px;
  text-align: center;
  white-space: nowrap;
  @media (max-width: 760px) {
    min-width: 0;
    flex: 1;
  }
`;

const TodayButton = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font: 600 12px ${tokens.fontSans};
  color: ${tokens.blue};
  cursor: pointer;
`;

const DateLabel = styled.label`
  font: 600 12px ${tokens.fontSans};
  color: ${tokens.slate};
`;

const DateInput = styled.input`
  padding: 6px 10px;
  border: 1px solid ${tokens.inputLine};
  border-radius: 6px;
  background: ${tokens.white};
  color: ${tokens.ink};
  font: 14px ${tokens.fontSans};
`;
